import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons/faAngleDown";
import {
  changeResultsFilters,
  selectChosenFlightsResults,
  selectResultsFilters,
} from "../searchFlightsSlice";
import { useOnClickOutside } from "../../../hooks/useOnClickOutside";

function AirlinesDropdown() {
  const dropdownRef = useRef(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  useOnClickOutside(
    dropdownRef,
    useCallback(() => setActive(false), [setActive])
  );
  const [airlines, setAirlines] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { airline } = useAppSelector(selectResultsFilters);
  const { flights } = useAppSelector(selectChosenFlightsResults);

  const options = ["", ...airlines.map((a) => a.code)];

  const onClick = () => {
    setActive(!active);
    if (!active) {
      setHighlightedIndex(options.indexOf(airline));
      setTimeout(() => menuRef.current?.focus(), 0);
    }
  };

  const onSelect = (value: string) => {
    dispatch(changeResultsFilters({ airline: value }));
    setActive(!active);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(
        (prev) => (prev - 1 + options.length) % options.length
      );
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(options[highlightedIndex]);
    } else if (e.key === "Escape") {
      setActive(false);
    }
  };

  useEffect(() => {
    setAirlines(extractAirlines(flights));
  }, [flights]);

  if (airlines.length <= 1) {
    return null;
  }
  return (
    <>
      <div className={`dropdown ${active ? " is-active" : ""}`}>
        <div className="dropdown-trigger">
          <button
            className="button"
            aria-haspopup="true"
            aria-controls="dropdown-menu"
            onClick={onClick}
          >
            <span>{t("HEADER_SORT_AIRLINE")}</span>
            <span className="icon">
              <FontAwesomeIcon icon={faAngleDown} aria-hidden="true" />
            </span>
          </button>
        </div>
        <div
          ref={menuRef}
          className="dropdown-menu"
          id="dropdown-menu"
          role="menu"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          <ul className="dropdown-content">
            <li>
              <a
                onMouseDown={() => onSelect("")}
                onMouseEnter={() => setHighlightedIndex(0)}
                href="#"
                tabIndex={-1}
                className={`dropdown-item${airline === "" ? " is-active" : ""}${
                  highlightedIndex === 0 && active ? " is-highlighted" : ""
                }`}
              >
                {t("ALL_AIRLINES")}
              </a>
            </li>
            {airlines.map((a, i) => {
              return (
                <li key={i}>
                  <a
                    onMouseDown={() => onSelect(a.code)}
                    onMouseEnter={() => setHighlightedIndex(i + 1)}
                    href="#"
                    tabIndex={-1}
                    className={`dropdown-item${
                      airline === a.code ? " is-active" : ""
                    }${
                      highlightedIndex === i + 1 && active
                        ? " is-highlighted"
                        : ""
                    }`}
                  >
                    {a.name}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      &nbsp;
    </>
  );
}

function extractAirlines(results) {
  const sortResult = results
    .map((result) => {
      return {
        name: result.airline,
        code: result.operatedBy,
      };
    })
    .filter((v, i, a) => a.findIndex((t) => t.code === v.code) === i);

  return sortResult.sort((a, b) => a.name.localeCompare(b.name));
}

export default AirlinesDropdown;
